import styled from "styled-components/macro";
import {
  H3,
  SmallSectionHeaderRegular,
} from "../../../../components/Typography/Typography";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useStoreState } from "../../../../util/util";
import { Form } from "../../../../layout/FormLayout";
import { FilePickerUncontrolled } from "../../../../components/FilePicker/FilePickerUncontrolled";
import { TemplateButtonContainer } from "../../OrganizationPage/ProductsList/CreatePimProductPage/CreateFromUploads/createProductFromUploads.util";
import {
  PrimaryButtonMedium,
  SecondaryButtonMedium,
} from "../../../../components/Buttons/Buttons";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { AxiosError } from "axios";
import axios from "axios";
import { StringParam, useQueryParams } from "use-query-params";
import { useNotifications } from "../../../../components/Notifications/NotificationsContext";
import { UploadPopups } from "../../OrganizationPage/ProductsList/CreatePimProductPage/CreateFromUploads/UploadPopups";
import { useLocation } from "react-router-dom";
import { useRoutePath } from "../../../../util/Routing";
import { screenSize } from "../../../../theme";
import type { PriceTierUploadSchema } from "./types";
import { spacingTokens } from "../../../../designTokens";

const UploadPriceTiersContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: ${({ theme }) => theme.resolve("spacing_2xl")} 0 22px;
`;

const Section = styled.section`
  display: flex;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
  flex-direction: column;
  margin: ${({ theme }) => theme.resolve("spacing_lg")} 0;
`;

const FileUploadSection = styled(Section)`
  width: 50%;
  @media ${screenSize.medium} {
    width: 100%;
  }
`;

const UploadSchema = z.object({
  price_tiers: z.instanceof(File),
});

/**
 * UploadPriceTiers Component
 *
 * Allows users to upload a CSV file to create or update price tiers in bulk for a storefront.
 * Handles file selection, form validation (using react-hook-form v6 and zod), upload submission, polling for processing status, and user notifications.
 *
 * Features:
 * - File upload (CSV) for price tiers
 * - Form validation and error handling
 * - Upload progress and completion notifications
 * - Polls backend for processing status and displays results
 * - Integrates with notification context for user feedback
 *
 * Backend Correlation:
 * - POST /v2/storefronts/:storefront_id/price-tiers/upload
 *   @see server/src/agilis/routes/v2.py (bulk price tier upload endpoint)
 * - GET /v2/storefronts/:storefront_id/price-tiers/bulk-upload/:uploadId/status
 *   @see server/src/agilis/routes/v2.py (bulk upload status endpoint)
 * - Response type: PriceTierUploadSchema
 *   @see server/src/agilis/models/price_tier.py
 *
 * Props:
 * - goBack: () => void — Callback to navigate back
 * - onCompleteUpload: () => void — Callback after upload completes
 *
 * Usage:
 * <UploadPriceTiers goBack={...} onCompleteUpload={...} />
 */
export const UploadPriceTiers = ({
  goBack,
  onCompleteUpload,
}: {
  goBack: () => void;
  onCompleteUpload: () => void;
}) => {
  const [showDialog, setShowDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const [uploadId, setUploadId] = useState<string>();
  const [uploadNumber, setUploadNumber] = useState<string>();
  const [status, setStatus] =
    useState<PriceTierUploadSchema["derived_status"]>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useTranslation();
  const { slug, storefront_id } = useStoreState();
  const [query, setQuery] = useQueryParams({
    uploadUUID: StringParam,
    uploadNumber: StringParam,
  });
  const { notifyError, notifySuccess, pollNotification } = useNotifications();
  const location = useLocation();
  const { adminPath } = useRoutePath();

  const methodsOfUseForm = useForm({
    resolver: zodResolver(
      UploadSchema.superRefine((form_value, ctx) => {
        const file = form_value.price_tiers;
        if (file && file.name !== `${slug}_price_tiers.csv`) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t(
              "Uploaded file name is different from expected file name, {{expectedFileName}}",
              { expectedFileName: `${slug}_price_tiers.csv` }
            ),
            path: ["price_tiers"],
          });
        }
      })
    ),
  });
  const { handleSubmit, errors, watch } = methodsOfUseForm;

  const getUploadNotificationMessage = useCallback(
    (status: PriceTierUploadSchema["derived_status"]) => {
      switch (status) {
        case "completed":
          return t(
            "Your files have been processed successfully, and your price tiers have been [created / updated]."
          );
        case "has_errors":
          return t(
            "There was an error processing your files and your price tiers could not be uploaded."
          );
        case "pending":
        case "processing":
          return t(
            "Your upload is being processed. You will be notified when it is complete."
          );
        default:
          return "";
      }
    },
    [t]
  );

  const getPollFn = useCallback(
    (uploadId: string) => {
      const pollFn = async () => {
        const {
          data: { derived_status, processed, number },
        } = await axios.get<PriceTierUploadSchema>(
          `/v2/storefronts/${storefront_id}/price-tiers/bulk-upload/${uploadId}/status`
        );
        const message = getUploadNotificationMessage(derived_status);
        return {
          shouldPoll:
            derived_status === "pending" || derived_status === "processing",
          status: derived_status,
          message,
          uploadId: uploadId,
          uploadNumber: number,
          numberOfNewProducts: processed,
        };
      };
      return pollFn;
    },
    [getUploadNotificationMessage, storefront_id]
  );

  const isInUploadPage = useMemo(() => {
    return location.pathname === `${adminPath}/tenants/bulk-price-tiers`;
  }, [location.pathname, adminPath]);

  const onCompletePolling = useCallback(
    (pollResult: {
      [prop: string]: string | number | boolean | object;
      shouldPoll: boolean;
      message: string;
    }) => {
      // Extract properties with fallback/defaults
      const { status, uploadId, uploadNumber, message, numberOfNewProducts } =
        pollResult as {
          status?: PriceTierUploadSchema["derived_status"];
          uploadId?: string;
          uploadNumber?: string;
          message: string;
          numberOfNewProducts?: number;
        };

      if (isInUploadPage) {
        setStatus(status);
        setDialogMessage(message);
        setUploadId(uploadId);
        setUploadNumber(uploadNumber);
        setShowDialog(true);
      } else {
        switch (status) {
          case "has_errors":
            return notifyError(
              t("Upload #{{uploadNumber}}: Upload failed", { uploadNumber })
            );
          case "completed":
            return notifySuccess(
              t(
                "Upload #{{uploadNumber}}: {{numberOfNewProducts}} price tiers were created",
                { uploadNumber, numberOfNewProducts }
              )
            );
        }
      }
    },
    [isInUploadPage, notifyError, notifySuccess, t]
  );

  const onSubmit = async (value: z.infer<typeof UploadSchema>) => {
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("file", value.price_tiers);
      const { data } = await axios.post<PriceTierUploadSchema>(
        `/v2/storefronts/${storefront_id}/price-tiers/bulk-upload`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      setQuery({ uploadUUID: data.upload_id, uploadNumber: data.number });
      setUploadId(data.upload_id);
      setUploadNumber(data.number);
      setShowDialog(true);
      setDialogMessage(getUploadNotificationMessage(data.derived_status));
      pollNotification({
        pollFn: getPollFn(data.upload_id),
        onComplete: onCompletePolling,
      });
    } catch (error) {
      const errorMessage = (error as AxiosError)?.response?.data?.message;
      notifyError(
        errorMessage
          ? errorMessage
          : t(
              "There was an error submitting your uploads. Please try again later."
            ),
        {
          error,
        }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const onComplete = () => {
    setShowDialog(false);
    setUploadId(undefined);
    setUploadNumber(undefined);
    setStatus(undefined);
    setDialogMessage("");
    onCompleteUpload();
  };

  const getStatus = (status: PriceTierUploadSchema["derived_status"]) => {
    if (status === "pending" || status === "processing") {
      return "in_progress";
    }
    if (status === "completed") {
      return "successful";
    }
    if (status === "has_errors") {
      return "failed";
    }
    return status;
  };

  useEffect(() => {
    if (query.uploadUUID && !uploadId) {
      setUploadId(query.uploadUUID);
      if (query.uploadNumber) {
        setUploadNumber(query.uploadNumber);
      }
      setShowDialog(true);
      setStatus("processing");
      setDialogMessage(getUploadNotificationMessage("processing"));
      pollNotification({
        pollFn: getPollFn(query.uploadUUID),
        onComplete: onCompletePolling,
      });
    }
  }, [
    getPollFn,
    getUploadNotificationMessage,
    onCompletePolling,
    pollNotification,
    query.uploadUUID,
    query.uploadNumber,
    uploadId,
  ]);

  const priceTiers = watch("price_tiers");

  const isDisabled = !priceTiers || Object.keys(errors).length > 0;

  return (
    <UploadPriceTiersContainer>
      <Section>
        <H3 style={{ margin: 0 }}>{t("Upload Price Tier Data")}</H3>
        <SmallSectionHeaderRegular style={{ margin: 0 }}>
          {t("Upload Price Tiers CSV to create and update price tiers ")}
        </SmallSectionHeaderRegular>
      </Section>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <FileUploadSection>
          <FilePickerUncontrolled
            name={"price_tiers"}
            accept=".csv"
            required
            methodsOfUseForm={methodsOfUseForm}
            placeHolderText={t("Drag and drop {{csv_file}}, OR", {
              csv_file: `${slug}_price_tiers.csv`,
            })}
          />
        </FileUploadSection>
        <Section>
          <TemplateButtonContainer
            style={{ marginTop: spacingTokens.spacing_3xl }}
          >
            <SecondaryButtonMedium
              disabled={!!uploadId || isSubmitting}
              onClick={goBack}
            >
              {t("Back")}
            </SecondaryButtonMedium>
            <PrimaryButtonMedium
              type="submit"
              disabled={isDisabled || isSubmitting}
              loading={isSubmitting}
            >
              {t("Upload")}
            </PrimaryButtonMedium>
          </TemplateButtonContainer>
        </Section>
      </Form>
      <UploadPopups
        showDialog={showDialog}
        setShowDialog={setShowDialog}
        onComplete={onComplete}
        message={dialogMessage}
        status={getStatus(status ?? "pending")}
        uploadId={uploadNumber ?? uploadId ?? ""}
      />
    </UploadPriceTiersContainer>
  );
};
